import { CheckCircle2, MailWarning } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { RecoveryFrame, RecoveryResult } from './PasswordRecoveryPages';

type VerificationState = 'checking' | 'verified' | 'invalid';

export function EmailVerificationPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<VerificationState>('checking');
  const [error, setError] = useState('');
  const user = searchParams.get('user') ?? '';
  const token = searchParams.get('token') ?? '';
  const displayState = !user || !token ? 'invalid' : state;
  const displayError = !user || !token
    ? 'This email verification link is incomplete.'
    : error;

  useEffect(() => {
    if (!user || !token) return;
    let active = true;
    void apiRequest('/auth/email-verification/confirm/', {
      method: 'POST',
      body: JSON.stringify({ user, token }),
    }).then(() => {
      if (active) setState('verified');
    }).catch((cause: unknown) => {
      if (!active) return;
      setError(getApiErrorMessage(cause));
      setState('invalid');
    });
    return () => { active = false; };
  }, [token, user]);

  return (
    <RecoveryFrame>
      {displayState === 'checking' ? (
        <section className="login-form recovery-result" aria-live="polite">
          <span className="recovery-result-icon"><InlineSpinner /></span>
          <h2>Verifying your email</h2>
          <p className="muted">Please wait while Brand Field Crew checks this one-time link.</p>
        </section>
      ) : displayState === 'verified' ? (
        <RecoveryResult
          icon={<CheckCircle2 size={44} />}
          title="Email verified"
          message="Your email address is verified. Return to Brand Field Crew and continue your BA profile."
          action={<a className="button primary" href="/profile">Continue to profile</a>}
        />
      ) : (
        <RecoveryResult
          icon={<MailWarning size={44} />}
          title="Verification link unavailable"
          message={displayError || 'This link is invalid or has expired. Sign in and request another verification email.'}
        />
      )}
    </RecoveryFrame>
  );
}
